/**
 * WP4b / #46608 — verification-define contracts for Workflows (including
 * intake), Controls, and Policies (versions + activation).
 *
 * Session-authenticated product UI API (`apiCall` / `useApi`). Distinct from
 * the token-scoped public catalog at `/api/verification/v1`.
 *
 * Environment: every call carries `env` (query) and/or
 * `x-excellent-verification-environment`. Object ids are namespaced
 * `demo:` / `real:` (WP2). Handlers must call `enforceVerificationEnvironment`.
 *
 * HG11 (#46595): mutations use existing ROLE_CAPABILITIES — no new
 * capabilities. Policy activation requires `policy:approve` (admin/owner;
 * owner escape is `verification:*`). Workflow intake + admit require
 * `api:write` (admin/owner; `api-client` is `api:read` only). Reads are
 * ungated at the Hono seam, same as WP4a overview.
 *
 * WP4a (overview/queue/findings/runs) is out of scope — do not edit
 * `verification-shell.ts`. Demo seed/reset is WP3. Schema is WP4g.
 */

import { z } from "zod";
import type { Operation } from "../types";

export const VERIFICATION_DEFINE_SCHEMA_VERSION = 1 as const;

const envName = z.enum(["demo", "real"]);
export const verificationDefineEnvQuery = z.object({
  env: envName.optional(),
  actorId: z.string().min(1).max(200).optional(),
});
const verificationStatus = z.enum(["PASS", "MISMATCH", "FAIL", "UNKNOWN", "NOT_RUN", "PENDING"]);
const consequence = z.enum(["low", "medium", "high", "critical"]);
const workflowStatus = z.enum(["draft", "needs-oracle", "admitted", "reject"]);
const policyStatus = z.enum(["draft", "active", "retired"]);
const policyIntent = z.enum(["tighten", "neutral", "loosen"]);
const evaluationMode = z.enum(["all_required_pass", "any_required_pass"]);
const frequency = z.enum(["continuous", "daily", "weekly", "monthly", "quarterly"]);
const namespacedId = z.string().min(1).max(200);
const iso = z.string().min(1);
const admissionStatus = z.enum(["admit", "reject", "needs-oracle"]);

const provenance = z.object({
  environment: envName,
  demoScoped: z.boolean(),
  trustChain: envName,
});

export type VerificationDefineEnvQuery = z.infer<typeof verificationDefineEnvQuery>;

export const verificationWorkflowDto = z.object({
  id: namespacedId,
  name: z.string().min(1),
  actor: z.string().min(1),
  verifier: z.string().min(1),
  oracle: z.string().nullable(),
  oracleIsIndependent: z.boolean(),
  consequence,
  evidence: z.string().min(1),
  status: workflowStatus,
  createdAt: iso,
  createdByActorId: namespacedId,
  rejectionReason: z.string().nullable(),
  admittedControlId: namespacedId.nullable(),
});
export type VerificationWorkflowDto = z.infer<typeof verificationWorkflowDto>;

export const verificationControlDto = z.object({
  id: namespacedId,
  ref: z.string().min(1),
  framework: z.string().min(1),
  family: z.string().min(1),
  title: z.string().min(1),
  ownerActorId: namespacedId.nullable(),
  consequence,
  status: verificationStatus,
  sourceIds: z.array(namespacedId),
  frequency,
  populationSize: z.number().int().nonnegative(),
  sampledSize: z.number().int().nonnegative(),
  openFindingCount: z.number().int().nonnegative(),
  activePolicyId: namespacedId.nullable(),
});
export type VerificationControlDto = z.infer<typeof verificationControlDto>;

export const verificationPolicyDto = z.object({
  id: namespacedId,
  controlId: namespacedId,
  version: z.number().int().positive(),
  status: policyStatus,
  evaluationMode,
  requiredRequirementIds: z.array(namespacedId).min(1),
  activatedAt: iso.nullable(),
  activatedByActorId: namespacedId.nullable(),
  createdByActorId: namespacedId,
  createdAt: iso,
  intent: policyIntent,
  expression: z.string().min(1),
  frozenCaseCount: z.number().int().nonnegative(),
  frozenCasesPassing: z.number().int().nonnegative(),
  blockedReason: z.string().nullable(),
});
export type VerificationPolicyDto = z.infer<typeof verificationPolicyDto>;

export const verificationIntakeAdmissionDto = z.object({
  status: admissionStatus,
  hasIndependentOracle: z.boolean(),
  reasons: z.array(z.string().min(1)),
});
export type VerificationIntakeAdmissionDto = z.infer<typeof verificationIntakeAdmissionDto>;

export const verificationIntakeBlockedDto = z.object({
  reason: z.string().min(1),
  message: z.string().min(1),
  fixCommand: z.string().nullable(),
});
export type VerificationIntakeBlockedDto = z.infer<typeof verificationIntakeBlockedDto>;

const idParams = z.object({ id: namespacedId });
export type VerificationDefineIdParams = z.infer<typeof idParams>;

const workflowsListQuery = verificationDefineEnvQuery.extend({
  status: workflowStatus.optional(),
});
export type VerificationWorkflowsListQuery = z.infer<typeof workflowsListQuery>;

export const verificationWorkflowsListResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_DEFINE_SCHEMA_VERSION),
  provenance,
  items: z.array(verificationWorkflowDto),
});
export type VerificationWorkflowsListResponse = z.infer<typeof verificationWorkflowsListResponse>;

export const verificationWorkflowGetResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_DEFINE_SCHEMA_VERSION),
  provenance,
  workflow: verificationWorkflowDto,
});
export type VerificationWorkflowGetResponse = z.infer<typeof verificationWorkflowGetResponse>;

export const verificationWorkflowIntakeBody = z.object({
  actorId: namespacedId,
  name: z.string().min(1),
  doc: z.string(),
  oracle: z.string().min(1).optional(),
  actor: z.string().min(1).optional(),
  verifier: z.string().min(1).optional(),
  evidence: z.string().min(1).optional(),
  consequence: consequence.optional(),
});
export type VerificationWorkflowIntakeBody = z.infer<typeof verificationWorkflowIntakeBody>;

export const verificationWorkflowIntakeResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_DEFINE_SCHEMA_VERSION),
  provenance,
  capability: z.string().min(1),
  admission: verificationIntakeAdmissionDto,
  blocked: verificationIntakeBlockedDto.nullable(),
  predicateCount: z.number().int().nonnegative(),
  workflow: verificationWorkflowDto,
});
export type VerificationWorkflowIntakeResponse = z.infer<typeof verificationWorkflowIntakeResponse>;

export const verificationWorkflowAdmitBody = z.object({
  actorId: namespacedId,
});
export type VerificationWorkflowAdmitBody = z.infer<typeof verificationWorkflowAdmitBody>;

export const verificationWorkflowAdmitResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_DEFINE_SCHEMA_VERSION),
  provenance,
  capability: z.string().min(1),
  workflow: verificationWorkflowDto,
});
export type VerificationWorkflowAdmitResponse = z.infer<typeof verificationWorkflowAdmitResponse>;

const controlsListQuery = verificationDefineEnvQuery.extend({
  status: verificationStatus.optional(),
  family: z.string().min(1).optional(),
});
export type VerificationControlsListQuery = z.infer<typeof controlsListQuery>;

export const verificationControlsListResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_DEFINE_SCHEMA_VERSION),
  provenance,
  items: z.array(verificationControlDto),
});
export type VerificationControlsListResponse = z.infer<typeof verificationControlsListResponse>;

export const verificationControlGetResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_DEFINE_SCHEMA_VERSION),
  provenance,
  control: verificationControlDto,
});
export type VerificationControlGetResponse = z.infer<typeof verificationControlGetResponse>;

const policiesListQuery = verificationDefineEnvQuery.extend({
  status: policyStatus.optional(),
  controlId: namespacedId.optional(),
});
export type VerificationPoliciesListQuery = z.infer<typeof policiesListQuery>;

export const verificationPoliciesListResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_DEFINE_SCHEMA_VERSION),
  provenance,
  items: z.array(verificationPolicyDto),
});
export type VerificationPoliciesListResponse = z.infer<typeof verificationPoliciesListResponse>;

export const verificationPolicyGetResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_DEFINE_SCHEMA_VERSION),
  provenance,
  policy: verificationPolicyDto,
});
export type VerificationPolicyGetResponse = z.infer<typeof verificationPolicyGetResponse>;

export const verificationPolicyVersionsResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_DEFINE_SCHEMA_VERSION),
  provenance,
  controlId: namespacedId,
  items: z.array(verificationPolicyDto),
});
export type VerificationPolicyVersionsResponse = z.infer<typeof verificationPolicyVersionsResponse>;

export const verificationPolicyActivateBody = z.object({
  actorId: namespacedId,
  rationale: z.string().min(1),
});
export type VerificationPolicyActivateBody = z.infer<typeof verificationPolicyActivateBody>;

export const verificationPolicyActivateResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_DEFINE_SCHEMA_VERSION),
  provenance,
  capability: z.string().min(1),
  policy: verificationPolicyDto,
  retiredPolicyId: namespacedId.nullable(),
});
export type VerificationPolicyActivateResponse = z.infer<typeof verificationPolicyActivateResponse>;

const workflowsListOp: Operation<
  void,
  VerificationWorkflowsListQuery,
  void,
  VerificationWorkflowsListResponse
> = {
  method: "GET",
  path: () => "/api/verification/workflows",
  query: workflowsListQuery,
  response: verificationWorkflowsListResponse,
};

const workflowsGetOp: Operation<
  VerificationDefineIdParams,
  VerificationDefineEnvQuery,
  void,
  VerificationWorkflowGetResponse
> = {
  method: "GET",
  path: ({ id }) => `/api/verification/workflows/${encodeURIComponent(id)}`,
  params: idParams,
  query: verificationDefineEnvQuery,
  response: verificationWorkflowGetResponse,
};

const workflowsIntakeOp: Operation<
  void,
  VerificationDefineEnvQuery,
  VerificationWorkflowIntakeBody,
  VerificationWorkflowIntakeResponse
> = {
  method: "POST",
  path: () => "/api/verification/workflows/intake",
  query: verificationDefineEnvQuery,
  body: verificationWorkflowIntakeBody,
  response: verificationWorkflowIntakeResponse,
  invalidates: [workflowsListOp],
};

const workflowsAdmitOp: Operation<
  VerificationDefineIdParams,
  VerificationDefineEnvQuery,
  VerificationWorkflowAdmitBody,
  VerificationWorkflowAdmitResponse
> = {
  method: "POST",
  path: ({ id }) => `/api/verification/workflows/${encodeURIComponent(id)}/admit`,
  params: idParams,
  query: verificationDefineEnvQuery,
  body: verificationWorkflowAdmitBody,
  response: verificationWorkflowAdmitResponse,
  invalidates: [workflowsListOp],
};

const controlsListOp: Operation<
  void,
  VerificationControlsListQuery,
  void,
  VerificationControlsListResponse
> = {
  method: "GET",
  path: () => "/api/verification/controls",
  query: controlsListQuery,
  response: verificationControlsListResponse,
};

const controlsGetOp: Operation<
  VerificationDefineIdParams,
  VerificationDefineEnvQuery,
  void,
  VerificationControlGetResponse
> = {
  method: "GET",
  path: ({ id }) => `/api/verification/controls/${encodeURIComponent(id)}`,
  params: idParams,
  query: verificationDefineEnvQuery,
  response: verificationControlGetResponse,
};

const policiesListOp: Operation<
  void,
  VerificationPoliciesListQuery,
  void,
  VerificationPoliciesListResponse
> = {
  method: "GET",
  path: () => "/api/verification/policies",
  query: policiesListQuery,
  response: verificationPoliciesListResponse,
};

const policiesGetOp: Operation<
  VerificationDefineIdParams,
  VerificationDefineEnvQuery,
  void,
  VerificationPolicyGetResponse
> = {
  method: "GET",
  path: ({ id }) => `/api/verification/policies/${encodeURIComponent(id)}`,
  params: idParams,
  query: verificationDefineEnvQuery,
  response: verificationPolicyGetResponse,
};

const policyVersionsOp: Operation<
  VerificationDefineIdParams,
  VerificationDefineEnvQuery,
  void,
  VerificationPolicyVersionsResponse
> = {
  method: "GET",
  path: ({ id }) => `/api/verification/policies/${encodeURIComponent(id)}/versions`,
  params: idParams,
  query: verificationDefineEnvQuery,
  response: verificationPolicyVersionsResponse,
};

const policiesActivateOp: Operation<
  VerificationDefineIdParams,
  VerificationDefineEnvQuery,
  VerificationPolicyActivateBody,
  VerificationPolicyActivateResponse
> = {
  method: "POST",
  path: ({ id }) => `/api/verification/policies/${encodeURIComponent(id)}/activate`,
  params: idParams,
  query: verificationDefineEnvQuery,
  body: verificationPolicyActivateBody,
  response: verificationPolicyActivateResponse,
  invalidates: [policiesListOp, controlsListOp],
};

export const verificationDefine = {
  workflowsList: workflowsListOp,
  workflowsGet: workflowsGetOp,
  workflowsIntake: workflowsIntakeOp,
  workflowsAdmit: workflowsAdmitOp,
  controlsList: controlsListOp,
  controlsGet: controlsGetOp,
  policiesList: policiesListOp,
  policiesGet: policiesGetOp,
  policyVersions: policyVersionsOp,
  policiesActivate: policiesActivateOp,
};
